import { z } from 'zod';
import { addDaysToKey, localDateKey, localParts, localTimeToUtc, weekdayOfKey } from './zoned.js';

/**
 * Vague 7 : **horaires structurés** du garage (en plus du texte libre `openingHours`, toujours affiché tel
 * quel) et bloc contact du portail : « Ouvert jusqu'à 18 h », « Ouvre lundi à 8 h », lien d'itinéraire.
 * Fermetures exceptionnelles (congés, jours fériés) : prévues plus tard, le texte libre les annonce.
 */

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export const Weekday = z.enum(WEEKDAYS);
export type Weekday = z.infer<typeof Weekday>;
export const WEEKDAY_LABELS: Record<Weekday, string> = {
  mon: 'Lundi',
  tue: 'Mardi',
  wed: 'Mercredi',
  thu: 'Jeudi',
  fri: 'Vendredi',
  sat: 'Samedi',
  sun: 'Dimanche',
};

const TimeOfDay = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Heure au format HH:MM attendue');

/** Plage d'ouverture d'un jour, sans passage de minuit (`open` < `close`). */
export const OpeningRange = z
  .strictObject({ open: TimeOfDay, close: TimeOfDay })
  .refine((r) => r.open < r.close, { message: 'L’heure de fermeture doit suivre l’heure d’ouverture', path: ['close'] });
export type OpeningRange = z.infer<typeof OpeningRange>;

/**
 * Jours ouverts et leurs plages (3 au plus, dans l'ordre, sans chevauchement). Un jour absent est fermé.
 * Fuseau : celui du garage (`garage_settings.timezone`).
 */
export const OpeningHoursSchedule = z
  .strictObject({
    days: z
      .array(z.strictObject({ day: Weekday, ranges: z.array(OpeningRange).min(1).max(3) }))
      .max(7),
  })
  .superRefine((s, ctx) => {
    const seen = new Set<string>();
    s.days.forEach((d, i) => {
      if (seen.has(d.day)) ctx.addIssue({ code: 'custom', path: ['days', i, 'day'], message: 'Jour en double' });
      seen.add(d.day);
      for (let k = 1; k < d.ranges.length; k++) {
        const prev = d.ranges[k - 1] as OpeningRange;
        const cur = d.ranges[k] as OpeningRange;
        if (cur.open < prev.close) {
          ctx.addIssue({ code: 'custom', path: ['days', i, 'ranges', k], message: 'Plages dans l’ordre, sans chevauchement' });
        }
      }
    });
  });
export type OpeningHoursSchedule = z.infer<typeof OpeningHoursSchedule>;

export interface OpeningStatus {
  openNow: boolean;
  /** Fermé : prochaine ouverture (dans les 7 jours), sinon `null`. */
  opensAt: string | null;
  /** Ouvert : fin de la plage en cours. */
  closesAt: string | null;
}

/** 0 = dimanche … 6 = samedi → clé du jour. */
const KEY_OF_WEEKDAY: Weekday[] = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];

/**
 * État d'ouverture à `now` dans le fuseau du garage. `null` sans horaires structurés (l'interface n'affiche
 * alors que le texte libre).
 */
export function openingStatus(
  schedule: OpeningHoursSchedule | null | undefined,
  timeZone: string,
  now: Date = new Date(),
): OpeningStatus | null {
  if (!schedule || schedule.days.length === 0) return null;
  const today = localDateKey(now, timeZone);
  const p = localParts(now, timeZone);
  const hhmm = `${String(p.h).padStart(2, '0')}:${String(p.min).padStart(2, '0')}`;
  const rangesOf = (key: string) =>
    schedule.days.find((d) => d.day === KEY_OF_WEEKDAY[weekdayOfKey(key)])?.ranges ?? [];

  for (const r of rangesOf(today)) {
    if (r.open <= hhmm && hhmm < r.close) {
      return { openNow: true, opensAt: null, closesAt: localTimeToUtc(today, r.close, timeZone).toISOString() };
    }
  }
  for (let offset = 0; offset <= 7; offset++) {
    const key = addDaysToKey(today, offset);
    for (const r of rangesOf(key)) {
      if (offset === 0 && r.open <= hhmm) continue;
      return { openNow: false, opensAt: localTimeToUtc(key, r.open, timeZone).toISOString(), closesAt: null };
    }
  }
  return { openNow: false, opensAt: null, closesAt: null };
}

/**
 * Lien d'itinéraire vers l'adresse du garage (bouton « Itinéraire » du portail). Choix : Google Maps, URL
 * universelle documentée `https://www.google.com/maps/dir/?api=1&destination=…`, **sans clé d'API ni
 * paramètre de suivi** : sur Android et iOS elle ouvre l'application de navigation du téléphone (sinon le
 * site), ce qu'attend un client qui vient chercher son véhicule. Seule l'adresse **publique** du garage y
 * figure, jamais une donnée du client, et le lien n'est suivi que si le client le touche. OpenStreetMap
 * (`openstreetmap.org/directions`) a été écarté : il n'ouvre pas l'application de navigation du téléphone.
 */
export function directionsUrl(address: string | null | undefined): string | null {
  const a = address?.replace(/\s+/g, ' ').trim();
  if (!a) return null;
  const url = new URL('https://www.google.com/maps/dir/');
  url.searchParams.set('api', '1');
  url.searchParams.set('destination', a);
  return url.href;
}
